// What can be done to everything ticked - ONE bar, used by every tree and
// list on the tracking page.
//
// It is a shared component rather than three similar ones on purpose
// (owner via the leader, 2026-09-20: "he should not have to learn which
// tree he is looking at"). The same words in the same order everywhere:
//
//   <count> in <n> selections | Set status | Isolate | Create a set |
//   Undo last change | Clear
//
// Status changes go through the preview / confirm / undo path, never
// straight to a write; the caller passes what to do, this only says it.
//
// "Link to a schedule activity" is deliberately NOT here, and must not be
// added for a set the next publish redefines (the New since last publish
// diff): the owner settled it himself on 2026-09-20 - a set that stops
// being true must not be attached to something durable, and "Add to a
// list, then link the list" is two visible steps instead of one invisible
// lie.
import React from "react";
import { Button, Dropdown, Space, Tooltip } from "antd";
import { popupInStage } from "./stagePopup";
import { STATUS_COLOR, STATUS_LABEL, STATUS_ORDER } from "./trackingTheme";

const MUTED = "#6B7280";
const plural = (n, word) => `${Number(n || 0).toLocaleString()} ${word}${Number(n) === 1 ? "" : "s"}`;

export default function BulkBar({
  count = null,             // elements the ticks stand for; null = still working it out
  selections = 0,           // how many ticked rows they came from
  countNote = "",           // e.g. a refusal, said instead of a number
  canEdit = false,
  busy = false,
  onStatus,                 // (status) => void
  isolated = false,
  onIsolate,
  onAddToList,
  onCustomCategory,
  onUndo,
  undoBusy = false,
  onClear,
  // WHAT THIS BAR ACTS ON, IN WORDS, ON THE BUTTON ITSELF (leader,
  // 2026-09-20). The selection can fall back to the whole set when
  // nothing is selected, and a set can be a million elements - so the
  // difference between "your selection" and "everything you ticked" must
  // be visible BEFORE the confirmation, not only inside it. If both
  // states read the same, the first time anyone notices is after a
  // million rows changed.
  actsOn = "",
}) {
  return (
    <Space size={6} wrap>
      <span style={{ fontSize: 12, color: MUTED }}>
        {countNote || (count === null ? "working out…" : plural(count, "element"))}
        {selections ? ` in ${plural(selections, "selection")}` : ""}
        {actsOn ? <span style={{ color: "#8c5a00" }}>{` · ${actsOn}`}</span> : ""}
      </span>
      {onStatus && (
        <Dropdown getPopupContainer={popupInStage}
          disabled={!canEdit || busy}
          trigger={["click"]}
          menu={{
            items: [
              { key: "_t", type: "group", label: actsOn ? `Set ${actsOn}` : "Set everything ticked" },
              ...STATUS_ORDER.map((k) => ({
                key: k,
                label: (
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[k] }} />
                    {STATUS_LABEL[k]}
                  </span>
                ),
              })),
            ],
            onClick: ({ key }) => { if (key !== "_t") onStatus(key); },
          }}
        >
          <Button size="small" type="primary" loading={busy}>Set status</Button>
        </Dropdown>
      )}
      {onIsolate && (
        <Button size="small" onClick={onIsolate}>{isolated ? "Show the whole model" : "Isolate"}</Button>
      )}
      {onAddToList && <Button size="small" onClick={onAddToList}>Create a set</Button>}
      {/* CUSTOM CATEGORY, WHERE THE OWNER LOOKED FOR IT (2026-10-03: "add a
          button next to set status/create set/create custom category"). The
          feature shipped 2026-09-24 and its only way in was a collapsed
          "Custom part categories" header in the right-hand panel, which is
          the same as not having shipped it. Optional, so the record tree's
          bar - which has no parts behind it - is unchanged. */}
      {onCustomCategory && (
        <Button size="small" onClick={onCustomCategory}>Create custom category</Button>
      )}
      {/* A door to the undo that is still there once the toast has gone
          (leader, 2026-09-20): this person's last change, once. */}
      {onUndo && (
        <Tooltip getPopupContainer={popupInStage} title="Put back the last status change you made">
          <Button size="small" loading={undoBusy} onClick={onUndo}>Undo last change</Button>
        </Tooltip>
      )}
      {onClear && <Button size="small" type="text" onClick={onClear}>Clear</Button>}
    </Space>
  );
}
